import type { CommitsBuildFailure, OperationData } from './types';
import { prettyPrintCommitsBuildFailure } from './types';
import { buildCommits } from './build-commits';
import { BuildExplorer } from './build-explorer';
import { Graph } from '../graph';
import { err, ok, Result } from 'neverthrow';

/**
 * A series of operations that an actor is wishing to apply.
 */
export type Commit = {
  readonly operations: OperationData[];
  readonly actorId: string;
  readonly message: string | null;
  readonly timestamp: Date;
  /**
   * The index this commit is intended to be appended at (for optimistic concurrency control).
   * Optional for backward compatibility.
   */
  readonly optimisticIndex?: number;
};

export type SavedCommit = Commit & {
  readonly index: number;
};

class GraphCompilerError {
  constructor(
    readonly err: CommitsBuildFailure,
    readonly at: Graph.Identity,
    readonly lastGoodBuild: BuildExplorer
  ) {}

  prettyPrint(): string {
    return prettyPrintCommitsBuildFailure(this.err);
  }
}

export type GraphCompilerResult = Result<BuildExplorer, GraphCompilerError>;

/**
 * Pretty prints a GraphCompilerResult error with comprehensive error information
 * @param error - The error struct from GraphCompilerResult
 * @returns A formatted string describing the error and context
 */
export const prettyPrintGraphCompilerError = (error: {
  err: CommitsBuildFailure;
  at: Graph.Identity;
  lastGoodBuild: BuildExplorer;
}): string => {
  const { err: failure, at, lastGoodBuild } = error;

  const failureMessage = prettyPrintCommitsBuildFailure(failure);
  const location = `at ${at.toString()}`;
  const lastGoodBuildInfo = `Last successful build had ${lastGoodBuild.symbols.length} symbols`;

  return `${failureMessage}\n${location}\n${lastGoodBuildInfo}`;
};

/**
 * GraphCompiler class that builds and manages a graph with operation history
 */
export class GraphCompiler {
  /**
   * Creates a new GraphCompiler
   */
  protected constructor(
    /** All of the commits, from which to build the graph. */
    readonly commits: Commit[] = [],
    /** Tool resolver for resolving tool information. */
    readonly toolResolver: Graph.IToolResolver
  ) {}

  static fromCommits(
    commits: Commit[],
    toolResolver: Graph.IToolResolver
  ): GraphCompiler {
    return new GraphCompiler(commits, toolResolver);
  }

  /**
   * Compiles the current state of the GraphCompiler into a Graph
   * @returns Result containing the compiled graph or error
   */
  compile(): GraphCompilerResult {
    const res = buildCommits(this.commits, { toolResolver: this.toolResolver });

    if (res.isOk()) {
      return ok(res.value);
    }

    const { err: e, at } = res.error;
    const commitsCopy = [...this.commits];

    while (commitsCopy.length > 0) {
      commitsCopy.pop();
      const lastGoodBuild = buildCommits(commitsCopy, {
        toolResolver: this.toolResolver,
      });
      if (lastGoodBuild.isOk()) {
        return err(new GraphCompilerError(e, at, lastGoodBuild.value));
      }
    }

    return err(new GraphCompilerError(e, at, BuildExplorer.empty()));
  }

  /**
   * Returns a clone of this compiler but loaded with the new commit.
   */
  commit(commit: Commit): GraphCompiler {
    if (commit.operations.length === 0) {
      return this; // No operations to apply
    }

    return GraphCompiler.fromCommits(
      [...this.commits, commit],
      this.toolResolver
    );
  }
}
